import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle, Circle, ArrowRight, Warning, CaretDown, CaretUp } from "@phosphor-icons/react";
import { InfoTip } from "@/components/common";

// Steps: key, label, desc, to, optional, perBeneficiary, skipTip
const STEPS = [
  { key: "add_student", label: "Add your beneficiary", to: "/students", desc: "Add the person your 529 is for." },
  { key: "add_account", label: "Add your 529 account", to: "/accounts", desc: "Enter the plan name and last 4 digits.", perBeneficiary: true },
  { key: "set_school_coa", label: "Set school & cost-of-attendance", to: "/students", desc: "Add the school, expected end date, and room & board caps for each beneficiary.", perBeneficiary: true },
  { key: "invite_student", label: "Invite your beneficiary", to: "/students", desc: "Give them a login to submit receipts.", optional: true, perBeneficiary: true,
    skipTip: "Skip this if the beneficiary won\u2019t be submitting receipts themselves \u2014 for example if you enter everything, if they\u2019re too young or haven\u2019t started school yet, or if they\u2019ve already finished. You can invite them any time from the Beneficiaries page." },
  { key: "approve_receipt", label: "Approve your first receipt", to: "/approvals", desc: "Review and approve a submitted expense." },
];

function isStepDone(status, key) {
  const v = status?.[key];
  if (v == null) return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "object") return v.complete === true;
  return false;
}

function getStepDetail(status, key) {
  const v = status?.[key];
  if (typeof v === "object" && v !== null) return v;
  return null;
}

export function OnboardingChecklist() {
  const qc = useQueryClient();
  const { selectedStudentId, selectStudent } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const { data: prefs } = useQuery({ queryKey: ["user-prefs"], queryFn: async () => (await api.get("/user-prefs")).data });
  const { data: status } = useQuery({ queryKey: ["onboarding-status"], queryFn: async () => (await api.get("/onboarding-status")).data });

  if (!prefs || !status) return null;
  if (prefs.onboarding_dismissed) return null;
  if (prefs.onboarding_completed_at) return null;

  const skipped = prefs.skipped_steps || [];
  const done = (key) => isStepDone(status, key) || skipped.includes(key);
  const doneCount = STEPS.filter((s) => done(s.key)).length;
  if (doneCount === STEPS.length) return null;

  const isAllBeneficiariesView = !selectedStudentId;
  const incompleteSteps = STEPS.filter((s) => !done(s.key));

  const skip = async (key) => {
    await api.put("/user-prefs", { skipped_steps: [...skipped, key] });
    qc.invalidateQueries({ queryKey: ["user-prefs"] });
  };

  const pct = (doneCount / STEPS.length) * 100;
  const Chevron = expanded ? CaretUp : CaretDown;

  return (
    <div className="mb-4 rounded-lg border border-[#034AA2]/30 bg-[#F4F7FB]" data-testid="onboarding-checklist">
      {/* Collapsed row — always visible */}
      <div className="flex flex-wrap items-center gap-3 px-4 py-3">
        {/* Mobile uses a deterministic first row; sm+ restores the original flex-1 layout. */}
        <div className="min-w-0 basis-full w-full sm:w-auto sm:basis-auto sm:flex-1">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-[#034AA2]">Setup guide</h3>
            <span className="text-xs font-semibold text-[#6b7280]">{doneCount} of {STEPS.length} complete</span>
          </div>
          <div className="mt-1.5 h-1.5 w-full max-w-xs rounded-full bg-[#C2C9D1]">
            <div className="h-1.5 rounded-full bg-[#034AA2] transition-all" style={{ width: `${pct}%` }} />
          </div>
        </div>
        {/* Keep mobile actions together on their own row so the progress block cannot compete for width. */}
        <div className="flex min-w-0 w-full items-center justify-end gap-3 sm:w-auto sm:justify-start">
          {/* Amber chips for incomplete tasks (collapsed only) */}
          {!expanded && incompleteSteps.length > 0 && (
            <div className="flex min-w-0 items-center gap-1.5 sm:shrink-0">
              {/* Mobile: show first chip only */}
              <span className="min-w-0 truncate sm:hidden rounded-full bg-[#D97706]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#D97706]" data-testid={`setup-chip-${incompleteSteps[0].key}`}>
                {incompleteSteps[0].label}
              </span>
              {incompleteSteps.length > 1 && (
                <span className="sm:hidden shrink-0 text-[11px] font-semibold text-[#D97706]">+{incompleteSteps.length - 1}</span>
              )}
              {/* sm+: show up to 2 chips */}
              <div className="hidden sm:flex items-center gap-1.5">
                {incompleteSteps.slice(0, 2).map((s) => (
                  <span key={s.key} className="rounded-full bg-[#D97706]/10 px-2.5 py-0.5 text-[11px] font-semibold text-[#D97706]" data-testid={`setup-chip-${s.key}`}>
                    {s.label}
                  </span>
                ))}
                {incompleteSteps.length > 2 && (
                  <span className="text-[11px] font-semibold text-[#D97706]">+{incompleteSteps.length - 2} more</span>
                )}
              </div>
            </div>
          )}
          <button onClick={() => setExpanded((v) => !v)}
            className="flex items-center gap-1 shrink-0 rounded-md px-2.5 py-1.5 text-xs font-bold text-[#034AA2] hover:bg-[#034AA2]/10 transition-colors"
            data-testid="onboarding-toggle">
            {expanded ? "Hide" : "Finish setup"} <Chevron size={14} weight="bold" />
          </button>
        </div>
      </div>

      {/* Expanded checklist */}
      {expanded && (
        <div className="border-t border-[#034AA2]/20 px-4 pb-4 pt-2 space-y-1.5">
          {STEPS.map((s) => {
            const isDone = done(s.key);
            const detail = getStepDetail(status, s.key);
            const isPerBeneficiary = s.perBeneficiary && detail;
            const showIncomplete = isPerBeneficiary && !isDone && isAllBeneficiariesView && detail.incomplete?.length > 0;

            return (
              <div key={s.key} className={`rounded-md px-2 py-1.5 text-sm ${isDone ? "opacity-60" : "bg-white border border-[#C2C9D1]"}`} data-testid={`onboarding-step-${s.key}`}>
                <div className="flex items-center gap-2.5">
                  {isDone
                    ? <CheckCircle size={18} weight="fill" className="shrink-0 text-[#077A39]" />
                    : <Circle size={18} weight="bold" className="shrink-0 text-[#C2C9D1]" />}
                  <div className="min-w-0 flex-1">
                    <span className={`font-semibold ${isDone ? "line-through text-[#6b7280]" : "text-[#0A0A0A]"}`}>{s.label}</span>
                    {!isDone && <span className="ml-1.5 text-xs text-[#6b7280]">{s.desc}</span>}
                    {!isDone && isPerBeneficiary && isAllBeneficiariesView && (
                      <span className="ml-1.5 text-xs font-semibold text-[#D97706]" data-testid={`onboarding-count-${s.key}`}>
                        {detail.completed} of {detail.total} beneficiar{detail.total === 1 ? "y" : "ies"}
                      </span>
                    )}
                  </div>
                  {!isDone && (
                    <div className="flex items-center gap-1.5 shrink-0">
                      {s.optional && (
                        <>
                          <button onClick={() => skip(s.key)} className="text-[10px] font-semibold text-[#6b7280] hover:underline" data-testid={`onboarding-skip-${s.key}`}>Skip</button>
                          {s.skipTip && <InfoTip text={s.skipTip} testid={`onboarding-skip-info-${s.key}`} side="top" size={12} />}
                        </>
                      )}
                      <Link to={s.to} className="flex items-center gap-0.5 text-xs font-semibold text-[#034AA2] hover:underline" data-testid={`onboarding-go-${s.key}`}>
                        Go <ArrowRight size={12} weight="bold" />
                      </Link>
                    </div>
                  )}
                </div>
                {showIncomplete && (
                  <div className="mt-1.5 ml-7 space-y-1" data-testid={`onboarding-incomplete-${s.key}`}>
                    {detail.incomplete.map((b) => (
                      <button key={b.id} onClick={() => selectStudent(b.id)} className="flex items-center gap-1 text-xs text-[#D97706] hover:underline" data-testid={`onboarding-incomplete-${s.key}-${b.id}`}>
                        <Warning size={12} weight="bold" /> {b.name} <ArrowRight size={10} weight="bold" />
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
